import { render } from "react-dom";
import { OrderProgressBar } from "./OrderProgressBar";
import * as currency from "@shopify/theme-currency";
import { OrderStatusUpdates } from "./OrderStatusUpdates";
import { OrderStatusLineItem, OrderLineItem } from "../types/OrderStatus";
import { OrderStatusReview } from "./OrderStatusReview";

(function init() {
  const root = document.querySelector("[data-order-status-widget]");
  if (root) render(<OrderStatus />, root);
})();

function LineItemPrice({ lineItem }) {
  if (lineItem.original_price != lineItem.final_price) {
    return (
      <dl className="flex gap-x-1">
        <dt>
          <span className="sr-only">Regular price</span>
        </dt>
        <dd>
          <s>{currency.formatMoney(lineItem.original_price, "${{amount_no_decimals}}")}</s>
        </dd>
        <dt>
          <span className="sr-only">Sale price</span>
        </dt>
        <dd>
          <span>{currency.formatMoney(lineItem.final_price, "${{amount_no_decimals}}")}</span>
        </dd>
      </dl>
    )
  }

  return (
    <p>{currency.formatMoney(lineItem.final_price, "${{amount_no_decimals}}")}</p>
  )
}

function LineItemImage({ lineItem }) {
  if (lineItem.variant.featured_image?.src) {
    return <img src={lineItem.variant.featured_image.src} alt={lineItem.variant.featured_image.alt} />
  } else {
    if (!lineItem.product.featured_image) return null;
    return <img src={lineItem.product.featured_image} alt={lineItem.variant.title} />
  }
}

function OrderStatus() {
  const data = JSON.parse(document.querySelector("[data-order-status]")?.innerHTML);

  const orderStatusFlows = {
    default: ["Order Placed", "In Production", "Shipped", "Delivered"],
    services: ["Return Label Sent", "Received at Warehouse", "In Production", "Shipped", "Delivered"]
  }

  if (!data) {
    return null;
  }

  return (
    <div>
      {data.order.line_items.map((lineItem: OrderLineItem) => {
        const lineItemStatus = data?.order_status_data?.line_items.find((statusLineItem: OrderStatusLineItem) => statusLineItem.variant_id === lineItem.variant.id);

        const statusFlow = lineItem.product.type === "Wigs" ? orderStatusFlows.default : orderStatusFlows.services;
        const isLastUpdateAnAlert = lineItemStatus?.updates ? lineItemStatus?.updates[0]?.type === "alert" : false;

        return (
          <div>            
            <div className="grid grid-cols-4 gap-x-[1rem] md:gap-x-[1.5rem] first:mt-[2rem] mt-[3.75rem]">
              <div className="col-span-1">
                <LineItemImage lineItem={lineItem} />
              </div>
              <div className="col-span-3 flex flex-col justify-center gap-y-[2rem]">
                <div>
                  <div className="md:flex items-center justify-between">
                    <h2 className="font-base normal-case text-[1.5rem] mb-1 md:mb-0">{lineItem.product.title}</h2>
                    <LineItemPrice lineItem={lineItem} />
                  </div>

                  <div className="xl:flex items-center gap-x-2 mt-2">
                    <p className="text-base-m text-black/[.65] leading-6">{lineItem.variant.title}</p>

                    {lineItem.properties.length > 0 &&
                      <details className="relative">
                        <summary className="text-base-m underline underline-offset-4 leading-6 padding-0 flex items-center text-black/[.65] cursor-pointer">
                          Show Customizations
                          <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-all"><path d="M9.99942 11.1411L14.1242 7.0163L15.3027 8.19481L9.99942 13.4981L4.69617 8.19481L5.87467 7.0163L9.99942 11.1411Z" fill="black"></path></svg>
                        </summary>
                        <div className="w-[22rem] absolute top-8 right-0 bg-white p-4 z-10">
                          <table className="w-full text-base-m leading-6">
                            {lineItem.properties.map((prop) => {
                              if (prop[0].charAt(0) === "_") return null;

                              const formattedPropertyName = prop[0].replace("_", " ");

                              return (
                                <tr className="py-0">
                                  <th className="capitalize text-black/[.65]">{formattedPropertyName}</th>
                                  <td className="break-words capitalize">{prop[1]}</td>
                                </tr>
                              )
                            })}
                          </table>

                          {data.order.note && 
                            <div className="border-t border-blush-400/75 text-base-m pt-4">
                              <p className="text-black/[.65]">Additional notes</p>
                              <p>{data.order.note}</p>
                            </div>
                          }
                        </div>
                      </details>
                    }
                  </div>
                </div>
               
                {lineItemStatus &&
                  <div className="hidden md:block">
                    <OrderStatusReview lineItem={lineItem} lineItemStatus={lineItemStatus} />
                    <OrderProgressBar lineItem={lineItem} lineItemStatus={lineItemStatus} statusFlow={statusFlow} isLastUpdateAnAlert={isLastUpdateAnAlert} />
                  </div>
                }
              </div>
            </div>

            {lineItemStatus &&
              <div className="block md:hidden">
                <OrderStatusReview lineItem={lineItem} lineItemStatus={lineItemStatus} />
                <OrderProgressBar lineItem={lineItem} lineItemStatus={lineItemStatus} statusFlow={statusFlow} isLastUpdateAnAlert={isLastUpdateAnAlert} />
              </div>
            }
            
            {lineItemStatus &&
              <OrderStatusUpdates lineItemStatus={lineItemStatus} />
            }
            
          </div>
        )
      })}
    </div>
  )
}